import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperSeparator,
  StepperTrigger,
} from '@/components/shadcn/stepper'
import { Check, LoaderCircleIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
// import { StepperComponentData } from './types'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { StepperComponentStepConfig, StepperComponentStepData } from '../types'
import { StepperStepContent } from './StepperStepContent'
import { StepperStepContextProvider } from './StepperStepContext'
import { FormValues } from '../../FormComponent/types'
import { EmptyState } from '@/components/ui/EmptyState'
import { useMemo } from 'react'

interface StepperComposedProps {
  orientation?: 'horizontal' | 'vertical'
  stepsLabel?: string
  stepsConfig?: StepperComponentStepConfig[]
  currentStep: number
  steps: StepperComponentStepData[]
  changeStepsHandlerId?: string
}
export function StepperComposed({
  orientation,
  stepsLabel,
  stepsConfig,
  currentStep,
  steps,
  changeStepsHandlerId,
}: StepperComposedProps) {
  const stepDataById = useMemo(() => new Map(steps.map((stepItem) => [stepItem.id, stepItem.data])), [steps])

  const getStepData = (stepId: string) => stepDataById.get(stepId) || ({} as FormValues)

  if ((stepsConfig?.length ?? 0) <= 1)
    return (
      <EmptyState
        title="No steps to display"
        description="Please change the tile filters and/or page filters to see the different options."
      />
    )
  const isVertical = orientation === 'vertical'
  return (
    <Stepper
      value={currentStep}
      // onValueChange={setCurrentStep}
      indicators={{
        completed: <Check className="size-4" />,
        loading: <LoaderCircleIcon className="size-4 animate-spin" />,
      }}
      className="space-y-8"
      orientation={orientation}
    >
      <StepperNav className="mb-15 gap-3">
        {stepsConfig?.map((stepConfig, index) => {
          const stepContextProps = {
            index,
            currentStep,
            step: stepConfig,
            stepsLabel,
            markdownContent: stepConfig.markdownContent,
            isVertical,
            stepData: getStepData(stepConfig.id),
            changeStepsHandlerId,
            canMarkAsComplete: stepConfig.action.canMarkAsComplete,
          }

          return (
            <StepperItem key={index} step={index + 1} className="relative flex-1 items-start">
              <StepperTrigger
                className={cn('flex grow items-start justify-center gap-4', { 'flex-col gap-1': !isVertical })}
                asChild
              >
                <StepperIndicator className="size-8 border data-[state=inactive]:border-border data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">
                  {stepConfig.icon ? (
                    <DynamicIcon
                      iconName={stepConfig.icon}
                      wrapperClassName="flex items-center justify-center aspect-square size-10"
                    />
                  ) : (
                    index + 1
                  )}
                </StepperIndicator>
                <StepperStepContextProvider {...stepContextProps}>
                  <StepperStepContent />
                </StepperStepContextProvider>
              </StepperTrigger>
              {stepsConfig.length > index + 1 && (
                <StepperSeparator
                  className={cn(
                    'absolute m-0',
                    {
                      'inset-y-0 left-4 top-[2.375rem] -order-1 m-0 -translate-x-1/2 group-data-[orientation=vertical]/stepper-nav:h-[calc(100%-2rem)]':
                        isVertical,
                    },
                    {
                      'inset-x-0 start-9 top-4 m-0 group-data-[orientation=horizontal]/stepper-nav:w-[calc(100%-2rem)] group-data-[orientation=horizontal]/stepper-nav:flex-none':
                        !isVertical,
                    }
                  )}
                />
              )}
            </StepperItem>
          )
        })}
      </StepperNav>
    </Stepper>
  )
}
